import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, FolderOpen, PlusCircle, Layers,
  Settings, GraduationCap, Scale, Calculator, LogOut,
  Receipt, User
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useModals } from '../context/ModalContext';
import FoxLogo from './FoxLogo';

// 🔥 DİL ENTEGRASYONU 🔥
import { useLanguage } from '../context/LanguageContext';

// 🔥 Tilki Bileşeni 🔥
import PlayfulFox from './PlayfulFox';

const Layout = ({ children }) => {
  const { logout } = useAuth();
  const { openModal } = useModals();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useLanguage(); // Dil fonksiyonunu al

  const menuItems = [
    { icon: LayoutDashboard, label: t('menu_dashboard') || 'Dashboard', path: '/dashboard' },
    { icon: FolderOpen, label: t('menu_archive') || 'Archive', path: '/archive' },
    { icon: Receipt, label: t('menu_create_invoice') || 'Create Invoice', path: '/create-invoice' },
    { icon: Layers, label: t('menu_templates') || 'Templates', path: '/templates' },
    { icon: Calculator, label: t('menu_salary') || 'Salary Calc', path: '/salary-calculator' },
    { icon: GraduationCap, label: t('menu_academy') || 'Academy', path: '/academy' },
    { icon: Scale, label: t('menu_procedures') || 'Procedures', path: '/official-procedures' },
    { icon: User, label: t('menu_profile') || 'Profile', path: '/profile' },
    { icon: Settings, label: t('menu_settings') || 'Settings', path: '/settings' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    // Arka planı App.css yönetiyor (body bg), burası şeffaf
    <div className="flex min-h-screen bg-transparent transition-colors duration-500 font-sans text-slate-100">
      
      {/* DESKTOP SIDEBAR - Soft Glass / Premium Navy */}
      <aside className="hidden lg:flex w-80 flex-col fixed h-full z-50 border-r border-white/5 bg-[#0B1120]/60 backdrop-blur-xl">
        
        <div className="p-8 pb-4">
           <div className="flex items-center gap-3 mb-8 cursor-pointer group" onClick={() => navigate('/dashboard')}>
              <div className="bg-gradient-to-br from-orange-600 to-orange-500 p-2.5 rounded-2xl shadow-lg shadow-orange-600/20 group-hover:scale-110 transition-transform duration-300">
                 <FoxLogo size={32} className="text-white" />
              </div>
              <div>
                 <h1 className="text-2xl font-black tracking-tighter uppercase italic text-white leading-none">
                   Büro<span className="text-orange-500">Fuchs</span>
                 </h1>
                 <p className="text-[9px] font-bold text-blue-200/60 uppercase tracking-[0.3em]">{t('digital_hub') || 'Digital Hub'}</p>
              </div>
           </div>
           
           <button 
             onClick={() => openModal('scan')}
             className="w-full h-14 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white rounded-2xl font-black uppercase tracking-widest text-xs flex items-center justify-center gap-3 transition-all active:scale-95 shadow-lg shadow-blue-900/30 border border-white/10"
           >
             <PlusCircle size={18} /> {t('menu_quick_scan') || 'Quick Scan'}
           </button>
        </div>

        <nav className="flex-1 px-6 space-y-2 overflow-y-auto custom-scrollbar pb-4">
           {menuItems.map((item) => (
             <button
               key={item.path}
               onClick={() => navigate(item.path)}
               className={`w-full flex items-center gap-4 px-4 py-3.5 rounded-2xl transition-all duration-300 group relative border ${
                 isActive(item.path) 
                 ? 'bg-blue-500/10 border-blue-500/20 text-white shadow-[0_0_20px_rgba(59,130,246,0.15)]' 
                 : 'border-transparent text-blue-200/50 hover:bg-white/5 hover:text-white'
               }`}
             >
               <item.icon size={20} className={`${isActive(item.path) ? 'text-orange-500 animate-pulse' : 'group-hover:scale-110 transition-transform'}`} />
               <span className="font-bold text-sm tracking-wide">{item.label}</span>
               {isActive(item.path) && <div className="absolute right-4 w-1.5 h-1.5 bg-orange-500 rounded-full shadow-[0_0_10px_orange]" />}
             </button>
           ))}
        </nav>

        <div className="p-6 border-t border-white/5 bg-[#0B1120]/40">
           <button 
             onClick={logout}
             className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-blue-200/40 hover:text-red-400 hover:bg-red-500/10 transition-colors text-xs font-bold uppercase tracking-wider"
           >
             <LogOut size={18} /> {t('menu_logout') || 'Log Out'}
           </button>
        </div>
      </aside>

      {/* MAIN CONTENT */}
      <div className="flex-1 lg:pl-80 flex flex-col min-h-screen relative z-0">
        {children}
        
        {/* GLOBAL FOOTER TEXT (Desktop) */}
        <div className="hidden lg:block absolute bottom-4 right-8 text-right pointer-events-none opacity-20 z-0">
             <p className="text-[9px] font-black uppercase tracking-[0.3em] text-white">
               BüroFuchs Intelligence Systems v3.0
             </p>
        </div>
      </div>

      {/* MOBILE MENU - Matching Theme */}
      <div className="lg:hidden fixed bottom-6 left-6 right-6 h-20 bg-[#0B1120]/90 border border-white/10 rounded-[2rem] shadow-2xl z-50 flex items-center justify-between px-6 backdrop-blur-xl">
         {[
            menuItems[0], // Dashboard
            menuItems[1], // Arşiv
            menuItems[2], // Fatura
            menuItems[7]  // Profil
         ].map((item) => (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={`p-3 rounded-full transition-all ${
                isActive(item.path) 
                ? 'bg-gradient-to-br from-orange-500 to-orange-600 text-white shadow-lg -translate-y-4 scale-110' 
                : 'text-blue-200/40 hover:text-white'
              }`}
            >
              <item.icon size={22} />
            </button>
         ))}
         
         <div className="absolute left-1/2 -translate-x-1/2 -top-6">
             <button 
                onClick={() => openModal('scan')} 
                className="p-4 bg-white text-[#0B1120] rounded-full shadow-xl border-4 border-[#0B1120] active:scale-95 transition-transform"
             >
                <PlusCircle size={28} />
             </button>
         </div>
      </div>

      {/* 🔥 Tilki (PlayfulFox) Bileşeni */}
      <PlayfulFox />

    </div>
  );
};

export default Layout;